import { Box, Container, Paper, Stack, Typography, keyframes } from "@mui/material";
import { useTenant } from "../../contexts/TenantContext";
import useRevealOnScroll, { REVEAL_EASING } from "../../hooks/useRevealOnScroll";
import { sectionSx } from "./sectionStyles";
import { gradientTextSx, sectionTitleSx, tint } from "../../utils/textStyles";
import { SECTION_SCROLL_MARGIN } from "../../layouts/layoutMetrics";
import { isDarkTheme, isNeonTheme } from "../../utils/themeTraits";

// The rows of a list arrive one after another once the pane scrolls into view — the same
// wave the gallery tiles make. Tied to the pane's reveal rather than to mounting: the posts
// are rendered with the page, and a cascade that ran at load would be over long before the
// guest scrolled down to it. The step is short and capped, or the end of a long list would
// still be fading in a second later.
const ITEM_DURATION_MS = 420;
const ITEM_BASE_DELAY_MS = 300;
const ITEM_STAGGER_MS = 35;
const ITEM_STAGGER_CAP = 16;

const itemIn = keyframes`
  from { opacity: 0; transform: translate3d(-10px, 0, 0); }
  to   { opacity: 1; transform: none; }
`;

/**
 * The dark themes (herevr, 2fadedbarbershop): black glass. Not the tinted glass "Rólunk" is drawn on and none of
 * its accent — no blue wash, no neon bar along the top: a near-black pane over a blurred
 * page, a faint sheen in its upper third and a hairline of light on its top edge, which is
 * what makes it read as glass rather than as a black box.
 *
 * `default` (brownsbs) keeps its paper card; a black pane would be a hole in a light page.
 */
const groupSurfaceSx = (displayConfig) => (isDarkTheme(displayConfig)
  ? {
      borderRadius: 3,
      color: displayConfig?.color,
      backgroundColor: "rgba(4, 6, 10, 0.62)",
      backgroundImage: "linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 34%)",
      backdropFilter: "blur(16px) saturate(140%)",
      WebkitBackdropFilter: "blur(16px) saturate(140%)",
      border: "1px solid rgba(255, 255, 255, 0.07)",
      boxShadow: "0 28px 60px -30px rgba(0, 0, 0, 0.9), inset 0 1px 0 rgba(255, 255, 255, 0.06)",
    }
  : sectionSx(displayConfig));

const drawerRule = (displayConfig) => (isDarkTheme(displayConfig)
  ? "rgba(255, 255, 255, 0.08)"
  : tint("#000", 0.08));

const Paragraph = ({ text }) => (
  <Typography fontSize={18} sx={{ whiteSpace: "pre-line" }}>
    {text}
  </Typography>
);

// A heading inside a post is a label within the drawer, not a title of its own, so it takes
// the primary colour rather than the action colour the post title is lettered in.
const Heading = ({ text, displayConfig }) => (
  <Typography variant="h5" fontWeight="bold"
    sx={gradientTextSx(displayConfig, displayConfig?.primaryColor)}>
    {text}
  </Typography>
);

// Rows in up to three columns, each ruled off by a hairline and marked with a small accent
// diamond — a bullet, not an icon: icons belong to the menu. The diamond glows on the dark
// theme and is flat on paper. The text of a row nudges right under the pointer.
const List = ({ items, displayConfig, isVisible }) => {
  const accent = displayConfig?.primaryColor;
  const dark = isDarkTheme(displayConfig);
  const neon = isNeonTheme(displayConfig);

  return (
    <Box component="ul"
      sx={{
        m: 0,
        p: 0,
        listStyle: "none",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
        columnGap: 4,
      }}>
      {items.map((item, index) => (
        <Box component="li" key={index}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: 1.25,
            borderBottom: `1px solid ${dark ? tint(accent, 0.2) : tint("#000", 0.08)}`,
            opacity: isVisible ? undefined : 0,
            animation: isVisible
              ? `${itemIn} ${ITEM_DURATION_MS}ms ${REVEAL_EASING} ` +
                `${ITEM_BASE_DELAY_MS + Math.min(index, ITEM_STAGGER_CAP) * ITEM_STAGGER_MS}ms both`
              : "none",
            "& .post-list-text": { transition: "transform 200ms ease" },
            "&:hover .post-list-text": { transform: "translateX(4px)" },
            "@media (prefers-reduced-motion: reduce)": {
              opacity: 1,
              animation: "none",
              "& .post-list-text": { transition: "none" },
              "&:hover .post-list-text": { transform: "none" },
            },
          }}>
          <Box aria-hidden
            sx={{
              width: 7,
              height: 7,
              flexShrink: 0,
              transform: "rotate(45deg)",
              backgroundColor: accent,
              boxShadow: neon ? `0 0 8px ${tint(accent, 0.8)}` : "none",
            }} />
          <Typography className="post-list-text" fontSize={16} fontWeight={500}>
            {item}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const Image = ({ src, alt, caption }) => (
  <Box component="figure" sx={{ m: 0 }}>
    <Box component="img" src={src} alt={alt} loading="lazy"
      sx={{ display: "block", width: "100%", borderRadius: 2 }} />
    {caption && (
      <Typography component="figcaption" fontSize={14} align="center" mt={1} sx={{ opacity: 0.7 }}>
        {caption}
      </Typography>
    )}
  </Box>
);

const renderBlock = (block, index, displayConfig, isVisible) => {
  switch (block.type) {
    case "paragraph":
      return <Paragraph key={index} text={block.text} />;
    case "heading":
      return <Heading key={index} text={block.text} displayConfig={displayConfig} />;
    case "list":
      return <List key={index} items={block.items} displayConfig={displayConfig} isVisible={isVisible} />;
    case "image":
      return <Image key={index} src={block.src} alt={block.alt} caption={block.caption} />;
    default:
      return null;
  }
};

/**
 * One post: a drawer of the shared section, always open — its title and its body, nothing to
 * unfold. The anchor is the drawer itself, so a menu entry scrolls to the title with the gap
 * `SECTION_SCROLL_MARGIN` leaves under the header.
 */
const BlogPostDrawer = ({ post, isFirst, isVisible }) => {
  const { displayConfig } = useTenant();
  const titleId = `${post.anchorId}-title`;

  return (
    <Box component="article" id={post.anchorId} aria-labelledby={titleId}
      sx={{
        scrollMarginTop: SECTION_SCROLL_MARGIN,
        px: { xs: 3, sm: 5 },
        py: { xs: 3, md: 4 },
        borderTop: isFirst ? "none" : `1px solid ${drawerRule(displayConfig)}`,
      }}>
      <Typography id={titleId} variant="h4" component="h2" fontWeight="bold" align="center"
        fontSize={{ xs: 24, md: 28 }} mb={3}
        sx={sectionTitleSx(displayConfig)}>
        {post.title}
      </Typography>

      <Stack spacing={3} sx={{ mx: "auto", width: { xs: "100%", md: "90%" } }}>
        {post.blocks.map((block, index) => renderBlock(block, index, displayConfig, isVisible))}
      </Stack>
    </Box>
  );
};

/**
 * The tenant's blog posts, as one section: a single pane as wide as "Rólunk", with every
 * post a drawer of it and nothing but a hairline between them — they read as one thing with
 * compartments, not as a row of separate cards.
 *
 * The pane slides in from the side. That was once forced: every drawer in it is a menu
 * target, and the menu scrolled with `scrollIntoView`, which aims at where an element is
 * *drawn* — a pane still offset downwards by a rising reveal had the scroll land that much
 * short. The menu now measures by layout (`scrollToSection`), so the sideways slide is a look,
 * not a constraint. The container clips it, as the opening-hours row does, so no horizontal
 * scrollbar flashes.
 */
const BlogPostsSection = ({ posts }) => {
  const { displayConfig } = useTenant();
  const { ref, isVisible, revealSx } = useRevealOnScroll({ direction: "left", distance: 120, duration: 1500 });

  if (posts.length === 0) {
    return null;
  }

  return (
    <Container maxWidth="xl" sx={{ overflowX: "clip" }}>
      <Paper ref={ref} elevation={0}
        sx={{
          ...groupSurfaceSx(displayConfig),
          overflow: "hidden",
          my: { xs: 3, lg: 4, xl: 5 },
          ...revealSx }}>
        {posts.map((post, index) => (
          <BlogPostDrawer key={post.anchorId} post={post} isFirst={index === 0} isVisible={isVisible} />
        ))}
      </Paper>
    </Container>
  );
};

export default BlogPostsSection;
